import { useState } from "react";
import moment from "moment";
import { Attendee, DemographicAnswer } from "../../types/attendee";
import { activeCard, cardTypeLabel, formatCard } from "./cardOnFile";

interface Props {
  attendee: Attendee;
}

export const AttendeeDetails = ({ attendee }: Props) => {
  const demographics = attendee.optional_datas ?? [];
  const [demoOpen, setDemoOpen] = useState(false);
  const card = activeCard(attendee);

  return (
    <div className="p-5 bg-gray-50 border-t border-gray-200 space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5">
        <Section title="Contact">
          <Row label="ID" value={String(attendee.id)} />
          <Row label="Email" value={attendee.email || "—"} wrap />
          <Row label="Phone" value={attendee.phone_number || "—"} />
          <Row label="Validation Code" value={attendee.validation_code || "—"} />
          <Row
            label="Card on File"
            value={card ? `${formatCard(card)}${cardTypeLabel(card) ? ` · ${cardTypeLabel(card)}` : ""}` : "None"}
            accent={card ? "text-gray-900" : "text-gray-500"}
          />
        </Section>

        <Section title="Event & Status">
          <Row label="Event" value={attendee.event?.name || "—"} wrap />
          <Row
            label="Status"
            value={attendee.is_suspended ? "Suspended" : attendee.is_active === false ? "Inactive" : "Active"}
            accent={
              attendee.is_suspended
                ? "text-red-700"
                : attendee.is_active === false
                  ? "text-gray-500"
                  : "text-green-700"
            }
          />
          <Row
            label="Created"
            value={attendee.created_at ? moment(attendee.created_at).format("MM/DD/YYYY h:mm a") : "—"}
          />
          <Row
            label="Updated"
            value={attendee.updated_at ? moment(attendee.updated_at).format("MM/DD/YYYY h:mm a") : "—"}
          />
          {attendee.note && <Row label="Note" value={attendee.note} wrap />}
        </Section>
      </div>

      <div className="bg-white rounded-md border border-gray-200">
        <button
          type="button"
          onClick={() => setDemoOpen((v) => !v)}
          disabled={demographics.length === 0}
          className={`w-full flex items-center justify-between px-4 py-2.5 text-left transition-colors ${
            demographics.length === 0 ? "cursor-default" : "hover:bg-gray-50"
          }`}
        >
          <div className="flex items-center gap-2">
            <svg
              className={`w-4 h-4 text-gray-500 transition-transform ${demoOpen ? "rotate-90" : ""} ${
                demographics.length === 0 ? "opacity-30" : ""
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
              Demographics
            </span>
            <span className="inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-700">
              {demographics.length}
            </span>
          </div>
          {demographics.length === 0 && (
            <span className="text-xs text-gray-400 italic">No responses recorded</span>
          )}
        </button>
        {demoOpen && demographics.length > 0 && (
          <div className="px-4 pb-4 pt-1 border-t border-gray-200 space-y-3">
            {demographics.map((q) => (
              <DemographicItem key={q.id} answer={q} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const DemographicItem = ({ answer }: { answer: DemographicAnswer }) => {
  const text = Array.isArray(answer.answer) ? answer.answer.join(", ") : answer.answer || "";
  return (
    <div>
      <div className="text-xs text-gray-500 break-words">{answer.question}</div>
      <div className={`text-sm tabular-nums break-words ${text ? "text-gray-900 font-medium" : "text-gray-400 italic"}`}>
        {text || "No response"}
      </div>
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold pb-2 mb-2 border-b border-gray-300">
      {title}
    </div>
    <dl className="divide-y divide-gray-200">{children}</dl>
  </div>
);

const Row = ({
  label,
  value,
  accent,
  wrap,
}: {
  label: string;
  value: string;
  accent?: string;
  wrap?: boolean;
}) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5">
    <dt className="text-xs uppercase tracking-wide text-gray-500 flex-shrink-0">{label}</dt>
    <dd
      className={`text-sm tabular-nums text-right ${accent ?? "text-gray-900"} font-medium ${
        wrap ? "break-words" : "whitespace-nowrap"
      }`}
    >
      {value}
    </dd>
  </div>
);
